import React from "react";

export interface BaseActionRendererProps {
  actionData: any;
  onExecuteAction?: (actionId: string) => void;
  actionId?: string;
  executingActions?: Set<string>;
  isExecuting?: boolean;
  onOverrideExecution?: () => void;
}

export interface EmailData {
  to?: string;
  subject?: string;
  body?: string;
}

export interface CalendarData {
  title?: string;
  description?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
}

export interface CoachPlanData {
  title?: string;
  overview?: string;
  goals?: Record<string, string>;
  actionItems?: string[];
  trackingMetrics?: string[];
}

export interface PriorityData {
  title?: string;
  description?: string;
  category?: string;
  priority?: string;
}

export interface TodoData {
  title?: string;
  description?: string;
  category?: string;
  priority?: string;
  deadline?: string;
}

// Utility function to get action data from different structures
export const getActionData = (actionData: any): any => {
  if (actionData.result) {
    // For executed actions, the data is in actionData.result
    const result = actionData.result;

    // Special handling for calendar actions to normalize the data structure
    if (result.start && result.end) {
      return {
        ...result,
        // Map the nested time structure to the expected flat structure
        startTime: result.start.dateTime,
        endTime: result.end.dateTime,
        timezone: result.start.timeZone || result.end.timeZone,
        // Keep other fields as they are
        title: result.summary || result.title,
        description: result.description,
        location: result.location,
        attendees: result.attendees,
      };
    }

    return result;
  } else {
    // For proposed actions, the data is in actionData.parameters or actionData
    return actionData.parameters || actionData;
  }
};

// Hook to handle execution override
export const useExecutionOverride = (onOverrideExecution?: () => void) => {
  const overrideCalled = React.useRef(false);

  React.useEffect(() => {
    if (onOverrideExecution && !overrideCalled.current) {
      onOverrideExecution();
      overrideCalled.current = true;
    }
  }, [onOverrideExecution]);
};
